import React, { useState } from "react";
import { Button } from "primereact/button";

/**
 * Pedido do voucher de uma reserva PAGA (Cotação Fase 2).
 *
 * COMPARTILHADO entre a página pública do cliente (/cotacao/:token) e o painel da agência,
 * que usam ROTAS DIFERENTES para o mesmo efeito — por isso o componente não conhece
 * nenhuma delas e recebe `enviar` pronto do chamador:
 *
 *   público  → POST /public/quote/:token/reserva/:bookingId/voucher   (actor: cliente)
 *   painel   → POST /quote/:id/reserva/:bookingId/voucher             (actor: agência + UserId)
 *
 * A separação é de AUDITORIA, não de permissão (BE 2026-08-07): se o painel chamasse a rota
 * pública, todo voucher disparado por um operador entraria na timeline como pedido do
 * cliente — e histórico é append-only na prática. Resposta e erros são idênticos nas duas.
 *
 * O PDF não é servido por URL: o BE gera sob demanda e ENVIA POR E-MAIL ao responsável
 * cadastrado — o destinatário sai do banco, nunca da requisição. Quem clica dispara o envio
 * mas não recebe o documento; por isso mostramos o e-mail MASCARADO que o BE devolve, para
 * confirmar o destino sem o endereço virar dado de tela.
 *
 * Após o sucesso o botão vira texto e não volta: reenvio em sequência só renderia 429.
 * Em erro, vira "Tentar novamente".
 *
 * @param {func}   enviar     () => Promise<{ email? }> — a chamada já ligada à reserva
 * @param {string} label      rótulo do botão (o cliente RECEBE; a agência ENVIA ao cliente)
 * @param {string} labelFeito texto de sucesso quando o BE não devolve o e-mail
 */
const VoucherButton = ({ enviar, label = "Receber voucher por e-mail", labelFeito = "Voucher enviado por e-mail." }) => {
	const [estado, setEstado] = useState("idle"); // idle | enviando | enviado | erro
	const [msg, setMsg] = useState("");

	const pedir = () => {
		setEstado("enviando");
		setMsg("");
		Promise.resolve(enviar())
			.then((r) => {
				setEstado("enviado");
				setMsg(r?.email ? `Voucher enviado para ${r.email}` : labelFeito);
			})
			.catch((e) => {
				const code = e?.response?.data?.error || e?.response?.data?.code;
				setEstado("erro");
				setMsg(
					code === "NOT_PAID" ? "O voucher fica disponível após a confirmação do pagamento."
					: code === "BOOKING_CANCELLED" ? "Esta reserva foi cancelada e não tem voucher."
					: code === "BOOKING_NOT_FOUND" ? "Reserva não encontrada. Fale com sua agência."
					: code === "NO_RECIPIENT" ? "Não há e-mail cadastrado para o envio. Fale com sua agência."
					: code === "EMAIL_FAILED" ? "Não conseguimos enviar agora. Tente novamente em instantes."
					// Só no caminho autenticado: cotação fora do escopo da agência (scopeWhere).
					// Na prática o operador não vê — a cotação nem apareceria na lista dele.
					: code === "NOT_FOUND" ? "Cotação não encontrada."
					: "Não foi possível enviar o voucher. Tente novamente."
				);
			});
	};

	if (estado === "enviado") {
		return <div style={{ color: "#2E9E5B", fontSize: "0.82rem", marginTop: 2 }}><i className="pi pi-check" style={{ fontSize: "0.72rem", marginRight: 4 }} />{msg}</div>;
	}
	return (
		<div>
			<Button label={estado === "erro" ? "Tentar novamente" : label}
				icon={estado === "erro" ? "pi pi-refresh" : "pi pi-envelope"} link
				style={{ padding: "4px 0", fontSize: "0.82rem" }}
				loading={estado === "enviando"} disabled={estado === "enviando"}
				onClick={pedir} />
			{estado === "erro" && msg ? <div style={{ color: "#C0392B", fontSize: "0.78rem" }}>{msg}</div> : null}
		</div>
	);
};

export default VoucherButton;
